import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useId,
  useMemo,
  useState,
  useEffect,
} from "react";
import { useSubMenu } from "../SubMenuContext";

const DESTAQUE_MS = 2400;

type Destaque = { id: string; n: number };

type AccordionGroupContextType = {
  openId: string | null;
  toggle: (id: string) => void;
  open: (id: string) => void;
  close: (id: string) => void;
  destaque: Destaque | null;
  highlight: (fieldId: string, slotId?: string) => void;
  register: (id: string, openByDefault?: boolean) => void;
  unregister: (id: string) => void;
  allowCollapse: boolean;
};

const AccordionGroupContext = createContext<AccordionGroupContextType | undefined>(undefined);

export function AccordionProvider({
  children,
  allowCollapse = true,
}: {
  children: ReactNode;
  allowCollapse?: boolean;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const idsRef = React.useRef(new Set<string>());

  const register = useCallback((id: string, openByDefault = false) => {
    idsRef.current.add(id);
    if (openByDefault) setOpenId(id);
  }, []);

  const unregister = useCallback((id: string) => {
    idsRef.current.delete(id);
    setOpenId((curr) => (curr === id ? null : curr));
  }, []);

  const toggle = useCallback(
    (id: string) => {
      setOpenId((curr) => {
        if (curr === id) return allowCollapse ? null : curr;
        return id;
      });
    },
    [allowCollapse]
  );

  const open = useCallback((id: string) => setOpenId(id), []);

  const close = useCallback(
    (id: string) => setOpenId((curr) => (curr === id && allowCollapse ? null : curr)),
    [allowCollapse]
  );

  const [destaque, setDestaque] = useState<Destaque | null>(null);

  const highlight = useCallback((fieldId: string, slotId?: string) => {
    if (slotId) setOpenId(slotId);

    setDestaque((curr) => ({ id: fieldId, n: curr?.id === fieldId ? curr.n + 1 : 0 }));
  }, []);

  useEffect(() => {
    if (!destaque) return;

    const timer = setTimeout(() => setDestaque(null), DESTAQUE_MS);

    return () => clearTimeout(timer);
  }, [destaque]);

  const value = useMemo(
    () => ({ openId, toggle, open, close, destaque, highlight, register, unregister, allowCollapse }),
    [openId, toggle, open, close, destaque, highlight, register, unregister, allowCollapse]
  );

  return <AccordionGroupContext.Provider value={value}>{children}</AccordionGroupContext.Provider>;
}

function useAccordionGroup() {
  const ctx = useContext(AccordionGroupContext);
  if (!ctx) throw new Error("Accordion components devem estar dentro de <AccordionProvider />");
  return ctx;
}

export function useSlots() {
  const { openId, open, close, toggle, destaque, highlight } = useAccordionGroup();
  const { open: abreMenu } = useSubMenu();

  const openSlot = (slotId: string, menuId?: string) => {
    if (menuId) abreMenu(menuId);

    open(slotId);
  };

  const highlightField = (fieldId: string, slotId?: string, menuId?: string) => {
    if (menuId) abreMenu(menuId);

    highlight(fieldId, slotId);
  };

  return {
    openSlotId: openId,
    openSlot,
    closeSlot: close,
    toggleSlot: toggle,
    highlightField,
    highlightedFieldId: destaque?.id ?? null,
  };
}

export function useFieldHighlight(id?: string) {
  const { destaque } = useAccordionGroup();

  return destaque && id && destaque.id === id ? destaque.n : null;
}

type AccordionItemContextType = {
  id: string;
  isOpen: boolean;
  toggle: () => void;
};

const AccordionItemContext = createContext<AccordionItemContextType | undefined>(undefined);

export function useAccordionItemState() {
  const ctx = useContext(AccordionItemContext);
  if (!ctx) throw new Error("Trigger deve ser usado dentro de <Container />");
  return ctx;
}

export function useAccordionItemAutoId(explicitId?: string, openByDefault = false) {
  const rid = useId();
  const id = explicitId ?? `acc-${rid}`;
  const { register, unregister } = useAccordionGroup();

  useEffect(() => {
    register(id, openByDefault);
    return () => unregister(id);
  }, [id, register, unregister]);

  return id;
}

export function useAccordionItem(explicitId?: string, openByDefault = false) {
  const { openId, toggle } = useAccordionGroup();
  const id = useAccordionItemAutoId(explicitId, openByDefault);

  return {
    id,
    isOpen: openId === id,
    toggle: () => toggle(id),
  };
}

export const AccordionItemProvider = AccordionItemContext.Provider;
